import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFolder, faFolderOpen, faFileCode, faChevronRight, faChevronDown,
  faGlobe, faSearch, faEye, faFilter, faExpandArrowsAlt, faTimes,
  faCopy, faTrash, faRedo, faCrosshairs, faLink, faSitemap,
  faRadiation, faSpinner, faBolt, faShieldAlt,
  faUnlock, faKey, faCode, faBug, faFileUpload, faTerminal,
  faBan, faPlus, faEyeSlash, faBullseye
} from '@fortawesome/free-solid-svg-icons';
import httpExchangeService from '../../services/httpExchangeService';
import proxyService from '../../services/proxyService';
import webVulnScanService from '../../services/webVulnScanService';
import * as webToolActions from '../../services/webToolActions';
import { toast } from 'react-toastify';
import ContextMenu from './ContextMenu';
import ScanConfigDialog from './ScanConfigDialog';

const methodColors = {
  GET: 'text-green-500',
  POST: 'text-yellow-500',
  PUT: 'text-blue-500',
  PATCH: 'text-purple-500',
  DELETE: 'text-red-500',
  OPTIONS: 'text-gray-400',
  HEAD: 'text-gray-400'
};

const SiteMapTree = ({ webTargetId, projectId, isDarkMode, onSelect, selectedFilter, proxyRunning, proxySessionId, onSendToRepeater, onSendToIntruder, onSendToAuthSmash, highlightColors, onHighlightChange, onDeletePath, onScanWithExposiq, activeScanHosts, onTabChange, externalFilter }) => {
  const [entries, setEntries] = useState([]);
  const [expandedPaths, setExpandedPaths] = useState(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [totalEndpoints, setTotalEndpoints] = useState(0);
  const [contextMenu, setContextMenu] = useState(null);
  const [scanConfigDialog, setScanConfigDialog] = useState(null);
  const [profileMap, setProfileMap] = useState({}); // id -> {name,color,role}
  // Out-of-scope hosts the proxy blocked (surfaced faded so the user sees what
  // they're missing and can add them to scope).
  const [outOfScopeHosts, setOutOfScopeHosts] = useState([]);
  const [showOutOfScope, setShowOutOfScope] = useState(true);

  const loadOutOfScope = useCallback(async () => {
    if (!proxySessionId) { setOutOfScopeHosts([]); return; }
    try { setOutOfScopeHosts(await proxyService.getOutOfScopeHosts(proxySessionId)); } catch { /* */ }
  }, [proxySessionId]);

  useEffect(() => {
    loadOutOfScope();
    if (!proxySessionId) return;
    const iv = setInterval(loadOutOfScope, 5000);
    return () => clearInterval(iv);
  }, [proxySessionId, loadOutOfScope]);

  const handleAddScope = async (host) => {
    if (!proxySessionId) { toast.info('Start the proxy first'); return; }
    try {
      await proxyService.addScopeHost(proxySessionId, host);
      setOutOfScopeHosts(prev => prev.filter(h => h.host !== host));
      toast.success(`Added ${host} to proxy scope (live — no restart)`);
    } catch (e) { toast.error('Failed to add to scope'); }
  };

  const handleRemoveScope = async (host) => {
    if (!proxySessionId) { toast.info('Start the proxy first'); return; }
    try {
      await proxyService.removeScopeHost(proxySessionId, host);
      toast.success(`Removed ${host} from proxy scope (data kept in tree)`);
    } catch (e) { toast.error('Failed to remove from scope'); }
  };
  // Multi-select: nodeKey -> { urls: string[], label }. Ctrl/Cmd/Shift+click a
  // node to add it to a scan batch; a toolbar then sends them all to the scanner.
  const [multiSelected, setMultiSelected] = useState(new Map());
  const pollRef = useRef(null);

  const isMultiSelected = (key) => multiSelected.has(key);
  const toggleMultiSelect = (key, urls, label) => {
    setMultiSelected(prev => {
      const next = new Map(prev);
      if (next.has(key)) next.delete(key);
      else next.set(key, { urls: [...new Set(urls.filter(Boolean))], label });
      return next;
    });
  };
  const clearMultiSelect = () => setMultiSelected(new Map());

  // Render the colored profile dots for a site-tree entry (+ anonymous).
  const ProfileDots = ({ entry }) => {
    const ids = (entry?.profiles || []).map(String).filter(Boolean);
    if (!ids.length && !entry?.anonymous) return null;
    return (
      <span className="inline-flex items-center gap-0.5 mr-1 align-middle">
        {entry?.anonymous && (
          <span className="inline-block w-2 h-2 rounded-full" style={{ backgroundColor: '#9CA3AF' }}
            title="Anonymous / not logged in" />
        )}
        {ids.map(id => {
          const p = profileMap[id];
          if (!p) return null;
          return <span key={id} className="inline-block w-2 h-2 rounded-full"
            style={{ backgroundColor: p.color || '#6B7280' }} title={`${p.name}${p.role ? ' — ' + p.role : ''}`} />;
        })}
      </span>
    );
  };

  const loadSiteTree = useCallback(async () => {
    if (!webTargetId) return;
    try {
      const data = await httpExchangeService.getSiteTree(webTargetId, externalFilter || undefined);
      // New shape: { tree, profiles }. Old shape: a bare array.
      const list = Array.isArray(data) ? data : (data?.tree || []);
      setEntries(list);
      setProfileMap(Array.isArray(data) ? {} : (data?.profiles || {}));
      // Count unique paths (not variants) for the endpoint counter
      const uniquePaths = new Set(list.map(e => `${e.host}|${e.path}`));
      setTotalEndpoints(uniquePaths.size || 0);
    } catch (err) {
      console.error('Error loading site tree:', err);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [webTargetId, externalFilter]);

  useEffect(() => {
    loadSiteTree();
  }, [loadSiteTree]);

  useEffect(() => {
    if (proxyRunning) {
      pollRef.current = setInterval(loadSiteTree, 4000);
    }
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [proxyRunning, loadSiteTree]);

  const tree = useMemo(() => {
    const hosts = {};
    const filtered = searchTerm
      ? entries.filter(e => e.path?.toLowerCase().includes(searchTerm.toLowerCase()) || e.host?.toLowerCase().includes(searchTerm.toLowerCase()))
      : entries;

    for (const entry of filtered) {
      const host = entry.host || 'unknown';
      if (!hosts[host]) hosts[host] = { children: {}, entries: [] };
      const parts = (entry.path || '/').split('/').filter(Boolean);
      let current = hosts[host];
      for (let i = 0; i < parts.length; i++) {
        const part = parts[i];
        if (!current.children[part]) {
          current.children[part] = { children: {}, entries: [], fullPath: '/' + parts.slice(0, i + 1).join('/') };
        }
        current = current.children[part];
      }
      current.entries.push(entry);
    }
    return hosts;
  }, [entries, searchTerm]);

  const toggleExpand = (key) => {
    const next = new Set(expandedPaths);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    setExpandedPaths(next);
  };

  const isSelected = (host, path) => {
    if (!selectedFilter) return false;
    if (path) return selectedFilter.host === host && selectedFilter.path === path;
    return selectedFilter.host === host && !selectedFilter.path;
  };

  const expandAll = () => {
    const keys = new Set();
    Object.keys(tree).forEach(host => {
      keys.add(`host|${host}`);
      const addChildKeys = (node, parentHost) => {
        Object.entries(node.children).forEach(([, child]) => {
          if (child.fullPath) keys.add(`${parentHost}|${child.fullPath}`);
          addChildKeys(child, parentHost);
        });
      };
      addChildKeys(tree[host], host);
    });
    setExpandedPaths(keys);
  };

  // Collect all URLs under a tree node (recursive)
  const collectUrls = useCallback((host, node) => {
    const urls = [];
    if (node) {
      for (const entry of (node.entries || [])) {
        urls.push(`https://${host}${entry.path || node.fullPath || '/'}`);
      }
      for (const child of Object.values(node.children || {})) {
        urls.push(...collectUrls(host, child));
      }
    }
    return [...new Set(urls)];
  }, []);

  // Get first exchange for a host+path (for send to repeater/intruder)
  const getFirstExchange = useCallback(async (host, path) => {
    try {
      const data = await httpExchangeService.getAll({ webTargetId, host, path: path || '/', limit: 1 });
      if (data.exchanges?.length > 0) {
        return await httpExchangeService.getById(data.exchanges[0]._id);
      }
    } catch {}
    return null;
  }, [webTargetId]);

  // Run a per-URL security tool on a tree node. Resolves the node's captured
  // exchange (by host+path, so query strings match) and calls the shared action.
  const runNodeTool = useCallback(async (host, path, tool) => {
    setContextMenu(null);
    const fullUrl = path ? `https://${host}${path}` : `https://${host}`;
    if (tool === 'curl') {
      const ex = await getFirstExchange(host, path);
      const cmd = ex ? webToolActions.buildCurl(ex) : `curl -X GET '${fullUrl}'`;
      try { await navigator.clipboard.writeText(cmd); toast.success('cURL copied to clipboard'); } catch { toast.error('Clipboard blocked'); }
      return;
    }
    if (tool === '403') {
      const ex = await getFirstExchange(host, path);
      await webToolActions.run403Bypass({
        webTargetId,
        method: ex?.request?.method || 'GET',
        url: ex?.request?.url || fullUrl
      });
      return;
    }
    // Exchange-based tools — resolve the capture first.
    const ex = await getFirstExchange(host, path);
    if (!ex?._id) { toast.info('No captured request found for this URL.'); return; }
    const target = { exchangeId: ex._id };
    const fn = {
      csrf: webToolActions.runCsrfPoc,
      upload: webToolActions.runUploadScan,
      extract: webToolActions.runExtractEndpoints,
      proto: webToolActions.runPrototypePollution,
      sso: webToolActions.runSsoAttack,
      beacon: webToolActions.runBeaconInjection
    }[tool];
    if (fn) await fn(target);
  }, [webTargetId, getFirstExchange]);

  // Extract links from response body of exchanges under this path
  const copyLinksInResponse = useCallback(async (host, path) => {
    try {
      const data = await httpExchangeService.getAll({ webTargetId, host, path: path || '/', limit: 10 });
      const links = new Set();
      for (const ex of (data.exchanges || [])) {
        const full = await httpExchangeService.getById(ex._id);
        const body = full?.response?.body || '';
        // Extract href, src, action attributes
        const urlRegex = /(?:href|src|action)=["']([^"']+)["']/gi;
        let m;
        while ((m = urlRegex.exec(body)) !== null) {
          let url = m[1];
          if (url.startsWith('//')) url = 'https:' + url;
          else if (url.startsWith('/')) url = `https://${host}${url}`;
          // eslint-disable-next-line no-script-url
          if (!url.startsWith('#') && !url.startsWith('javascript:') && !url.startsWith('data:')) {
            links.add(url);
          }
        }
      }
      const text = [...links].sort().join('\n');
      await navigator.clipboard.writeText(text);
      return links.size;
    } catch { return 0; }
  }, [webTargetId]);

  // Delete all exchanges under a host+path
  const handleDeletePath = useCallback(async (host, path) => {
    try {
      const data = await httpExchangeService.getAll({ webTargetId, host, path: path || undefined, limit: 500 });
      const ids = (data.exchanges || []).map(ex => ex._id);
      if (ids.length > 0) {
        await httpExchangeService.bulkDeleteByIds(ids);
        loadSiteTree();
        onDeletePath?.();
      }
    } catch {}
  }, [webTargetId, loadSiteTree, onDeletePath]);

  // Handle active vulnerability scan
  const handleActiveScan = useCallback((host, path, scanType) => {
    const fullPath = path ? `https://${host}${path}` : `https://${host}`;
    const scope = {
      type: path ? 'path' : 'host',
      value: fullPath,
      includeChildren: true
    };

    const scanName = `${scanType.toUpperCase()} - ${path || host}`;

    // Show configuration dialog
    setScanConfigDialog({
      scanName,
      scanType,
      targetScope: scope,
      insertionPoints: ['url_params', 'body_params', 'headers']
    });

    // Close context menu
    setContextMenu(null);
  }, []);

  // Send all multi-selected nodes' URLs to the active scanner as one batch.
  const handleMultiScan = useCallback(() => {
    const urls = [...new Set([...multiSelected.values()].flatMap(v => v.urls))];
    if (urls.length === 0) return;
    setScanConfigDialog({
      scanName: `Active scan - ${urls.length} URL${urls.length === 1 ? '' : 's'}`,
      scanType: 'all',
      targetScope: { type: 'urls', values: urls, includeChildren: false },
      insertionPoints: ['url_params', 'body_params', 'headers']
    });
  }, [multiSelected]);

  const handleStartScan = useCallback(async (config) => {
    try {
      const scan = await webVulnScanService.createScan({
        webTargetId,
        projectId,
        scanType: config.scanType || scanConfigDialog.scanType,
        targetScope: scanConfigDialog.targetScope,
        scanName: config.scanName,
        insertionPoints: config.insertionPoints,
        timing: config.timing,
        payloadFilter: config.payloadFilter,
        authProfileId: config.authProfileId || undefined
      });

      await webVulnScanService.startScan(scan._id);
      toast.success('Vulnerability scan started');

      // Close dialog
      setScanConfigDialog(null);

      // Navigate to scanner tab
      if (onTabChange) {
        onTabChange('scanner');
      }
    } catch (error) {
      console.error('Failed to start scan:', error);
      toast.error('Failed to start vulnerability scan');
    }
  }, [webTargetId, projectId, scanConfigDialog, onTabChange]);

  // Build context menu for any node
  const handleTreeContextMenu = (e, host, path, node) => {
    e.preventDefault();
    e.stopPropagation();

    const colorKey = path ? `${host}|${path}` : host;
    const currentColor = highlightColors?.[colorKey] || null;
    const fullUrl = path ? `https://${host}${path}` : `https://${host}`;

    // Collect sub-URLs if this is a branch node
    let subUrls = [];
    if (node) {
      subUrls = collectUrls(host, node);
    } else if (tree[host]) {
      // Host-level: collect all URLs under this host
      subUrls = collectUrls(host, tree[host]);
    }

    const items = [
      { label: 'Filter by this', icon: faFilter, onClick: () => onSelect?.({ host, path }) },
      { label: 'Expand All', icon: faExpandArrowsAlt, onClick: expandAll },
      { divider: true },
      { label: 'Copy URL', icon: faCopy, onClick: () => navigator.clipboard.writeText(fullUrl) },
      { label: 'Copy as cURL', icon: faTerminal, onClick: () => runNodeTool(host, path, 'curl') },
      ...(subUrls.length > 1 ? [{
        label: `Copy URLs from sub-items (${subUrls.length})`,
        icon: faSitemap,
        onClick: () => navigator.clipboard.writeText(subUrls.join('\n'))
      }] : []),
      { label: 'Copy links in response', icon: faLink, onClick: () => copyLinksInResponse(host, path) },
      { divider: true },
      {
        label: 'Send to Repeater', icon: faRedo,
        onClick: async () => {
          const ex = await getFirstExchange(host, path);
          if (ex) onSendToRepeater?.(ex);
        }
      },
      {
        label: 'Send to Intruder', icon: faCrosshairs,
        onClick: async () => {
          const ex = await getFirstExchange(host, path);
          if (ex) onSendToIntruder?.(ex);
        }
      },
      {
        label: 'Send to AuthSmash', icon: faShieldAlt,
        onClick: () => {
          if (onSendToAuthSmash) {
            onSendToAuthSmash(subUrls);
          }
        }
      },
      // ExposIQ scan options (host-level nodes only)
      ...(!path && onScanWithExposiq ? [
        { divider: true },
        { label: 'Quick Scan with ExposIQ (~5 min)', icon: faRadiation, onClick: () => onScanWithExposiq(host, 'quick') },
        { label: 'Standard Scan with ExposIQ (~15 min)', icon: faRadiation, onClick: () => onScanWithExposiq(host, 'standard') },
        { label: 'Full Scan with ExposIQ (~45 min)', icon: faRadiation, onClick: () => onScanWithExposiq(host, 'full') },
      ] : []),
      // Active vulnerability scans
      { divider: true },
      {
        label: 'Active Scans',
        icon: faBolt,
        submenu: [
          { label: 'All Active Attacks', onClick: () => handleActiveScan(host, path, 'all') },
          { divider: true },
          { label: 'XSS (Cross-Site Scripting)', onClick: () => handleActiveScan(host, path, 'xss') },
          { label: 'SQL Injection', onClick: () => handleActiveScan(host, path, 'sqli') },
          { label: 'CSRF', onClick: () => handleActiveScan(host, path, 'csrf') },
          { label: 'SSRF', onClick: () => handleActiveScan(host, path, 'ssrf') },
          { label: 'OS Command Injection', onClick: () => handleActiveScan(host, path, 'rce') },
          { label: 'Path Traversal', onClick: () => handleActiveScan(host, path, 'lfi') },
          { label: 'HTTP Request Smuggling', onClick: () => handleActiveScan(host, path, 'http_smuggling') }
        ]
      },
      {
        label: 'Security Tests',
        icon: faShieldAlt,
        submenu: [
          { label: 'Test 403/401 Bypass', icon: faUnlock, onClick: () => runNodeTool(host, path, '403') },
          { label: 'Test SSO Attacks (SAML/OAuth)', icon: faKey, onClick: () => runNodeTool(host, path, 'sso') },
          { label: 'Test Prototype Pollution', icon: faBug, onClick: () => runNodeTool(host, path, 'proto') },
          { label: 'Test File Upload Bypass', icon: faFileUpload, onClick: () => runNodeTool(host, path, 'upload') },
          { label: 'Beacon Injection', icon: faBullseye, onClick: () => runNodeTool(host, path, 'beacon') },
          { label: 'Extract Endpoints (JS/HTML)', icon: faCode, onClick: () => runNodeTool(host, path, 'extract') },
          { label: 'Generate CSRF PoC', icon: faShieldAlt, onClick: () => runNodeTool(host, path, 'csrf') },
        ]
      },
      { divider: true },
      {
        colorPicker: true,
        label: 'Highlight',
        currentColor,
        onSelect: (color) => onHighlightChange?.(colorKey, color)
      },
      ...((!path && proxySessionId) ? [
        { divider: true },
        { label: 'Remove host from proxy scope', icon: faBan, onClick: () => handleRemoveScope(host) }
      ] : []),
      { divider: true },
      {
        label: 'Delete', icon: faTrash, danger: true,
        onClick: () => handleDeletePath(host, path)
      },
      { label: 'Clear Filter', icon: faTimes, onClick: () => onSelect?.(null) }
    ];

    setContextMenu({ position: { x: e.clientX, y: e.clientY }, items });
  };

  // Resolve node color — check self, then parent paths
  const resolveColor = (host, path) => {
    if (!highlightColors) return null;
    // Check exact match
    const exact = highlightColors[`${host}|${path}`];
    if (exact) return exact;
    // Check host-level
    const hostColor = highlightColors[host];
    if (hostColor) return hostColor;
    // Check parent paths
    if (path) {
      const parts = path.split('/').filter(Boolean);
      for (let i = parts.length - 1; i >= 0; i--) {
        const parentPath = '/' + parts.slice(0, i).join('/');
        const pc = highlightColors[`${host}|${parentPath}`];
        if (pc) return pc;
      }
    }
    return null;
  };

  // Render a single parameter-variant sub-item (Burp-style: "METHOD param1=val1&param2=val2")
  const renderVariantNode = (entry, host, path, depth) => {
    const variantKey = `${host}|${path}|${entry.method}|${entry.paramSig || ''}`;
    const label = entry.sampleQuery
      ? `${entry.sampleQuery}`.substring(0, 120) + (entry.sampleQuery.length > 120 ? '…' : '')
      : (entry.sampleContentType?.includes('json') ? '{JSON}' : entry.sampleContentType?.includes('form') ? '{FORM}' : '');
    const fullUrl = `https://${host}${path}${entry.sampleQuery ? '?' + entry.sampleQuery : ''}`;

    return (
      <div
        key={variantKey}
        className={`flex items-center gap-1 pr-2 py-0.5 cursor-pointer text-xs ${
          isDarkMode ? 'hover:bg-gray-700/50 text-gray-400' : 'hover:bg-gray-50 text-gray-500'
        }`}
        style={{ paddingLeft: depth * 14 + 8 }}
        title={fullUrl}
        onClick={() => onSelect?.({ host, path })}
        onContextMenu={(e) => handleVariantContextMenu(e, host, path, entry)}
      >
        <span className="w-3 flex-shrink-0" />
        <span className={`text-[8px] font-mono flex-shrink-0 ${methodColors[entry.method] || 'text-gray-400'}`}>{entry.method}</span>
        <span className="truncate flex-1 font-mono text-[10px]">{label || '(no params)'}</span>
        <ProfileDots entry={entry} />
        {entry.requestCount > 1 && (
          <span className={`text-[8px] ${isDarkMode ? 'text-gray-600' : 'text-gray-400'}`}>{entry.requestCount}×</span>
        )}
      </div>
    );
  };

  // Context menu for a specific parameter variant (sends THAT exchange to repeater)
  const handleVariantContextMenu = (e, host, path, entry) => {
    e.preventDefault();
    e.stopPropagation();
    const fullUrl = `https://${host}${path}${entry.sampleQuery ? '?' + entry.sampleQuery : ''}`;
    const items = [
      { label: 'Filter by this', icon: faFilter, onClick: () => onSelect?.({ host, path }) },
      { divider: true },
      { label: 'Copy URL', icon: faCopy, onClick: () => navigator.clipboard.writeText(fullUrl) },
      { label: 'Copy as cURL', icon: faTerminal, onClick: async () => {
        const ex = entry.sampleId
          ? await httpExchangeService.getById(entry.sampleId)
          : await getFirstExchange(host, path);
        if (ex) {
          const cmd = webToolActions.buildCurl(ex);
          try { await navigator.clipboard.writeText(cmd); toast.success('cURL copied to clipboard'); } catch { toast.error('Clipboard blocked'); }
        }
      }},
      { divider: true },
      {
        label: 'Send to Repeater', icon: faRedo,
        onClick: async () => {
          const ex = entry.sampleId
            ? await httpExchangeService.getById(entry.sampleId)
            : await getFirstExchange(host, path);
          if (ex) onSendToRepeater?.(ex);
        }
      },
      {
        label: 'Send to Intruder', icon: faCrosshairs,
        onClick: async () => {
          const ex = entry.sampleId
            ? await httpExchangeService.getById(entry.sampleId)
            : await getFirstExchange(host, path);
          if (ex) onSendToIntruder?.(ex);
        }
      },
      {
        label: 'Send to AuthSmash', icon: faShieldAlt,
        onClick: () => onSendToAuthSmash?.([fullUrl])
      },
      { divider: true },
      {
        label: 'Active Scans',
        icon: faBolt,
        submenu: [
          { label: 'All Active Attacks', onClick: () => handleActiveScan(host, path, 'all') },
          { divider: true },
          { label: 'XSS', onClick: () => handleActiveScan(host, path, 'xss') },
          { label: 'SQL Injection', onClick: () => handleActiveScan(host, path, 'sqli') },
          { label: 'SSRF', onClick: () => handleActiveScan(host, path, 'ssrf') },
          { label: 'OS Command Injection', onClick: () => handleActiveScan(host, path, 'rce') },
          { label: 'Path Traversal', onClick: () => handleActiveScan(host, path, 'lfi') }
        ]
      }
    ];
    setContextMenu({ position: { x: e.clientX, y: e.clientY }, items });
  };

  const renderPathNode = (node, name, host, depth) => {
    const hasChildren = Object.keys(node.children).length > 0;
    const hasVariants = node.entries.length > 1;
    const isExpandable = hasChildren || hasVariants;
    const nodeKey = `${host}|${node.fullPath}`;
    const isExpanded = expandedPaths.has(nodeKey);
    const selected = isSelected(host, node.fullPath);
    // Aggregate methods from all variant entries (each entry now has single 'method')
    const methods = [...new Set(node.entries.map(e => e.method).filter(Boolean))];
    // Fallback: legacy entries may still have 'methods' array
    if (methods.length === 0) {
      const legacyMethods = [...new Set(node.entries.flatMap(e => e.methods || []))];
      methods.push(...legacyMethods);
    }
    const nodeColor = resolveColor(host, node.fullPath);
    const nodeProfileIds = [...new Set(node.entries.flatMap(e => (e.profiles || []).map(String)))];
    const nodeAnon = node.entries.some(e => e.anonymous);
    const nodeFullUrl = `https://${host}${node.fullPath || '/'}`;
    const multiSel = isMultiSelected(nodeKey);
    const nodeUrls = hasChildren ? collectUrls(host, node) : [nodeFullUrl];

    return (
      <div key={nodeKey}>
        <div
          className={`flex items-center gap-1 pr-2 py-0.5 cursor-pointer text-xs ${
            multiSel
              ? (isDarkMode ? 'bg-emerald-900/40 ring-1 ring-emerald-500 text-emerald-200' : 'bg-emerald-50 ring-1 ring-emerald-400 text-emerald-800')
              : selected
                ? (isDarkMode ? 'bg-teal-900/30 text-teal-300' : 'bg-teal-50 text-teal-700')
                : (isDarkMode ? 'hover:bg-gray-700 text-gray-300' : 'hover:bg-gray-100 text-gray-700')
          }`}
          style={{
            paddingLeft: depth * 14 + 8,
            ...(nodeColor && !selected && !multiSel ? { borderLeft: `3px solid ${nodeColor}`, backgroundColor: `${nodeColor}15` } : {})
          }}
          onClick={(e) => {
            if (e.ctrlKey || e.metaKey || e.shiftKey) {
              e.preventDefault();
              toggleMultiSelect(nodeKey, nodeUrls, name);
              return;
            }
            onSelect?.({ host, path: node.fullPath });
            if (isExpandable) toggleExpand(nodeKey);
          }}
          onContextMenu={(e) => handleTreeContextMenu(e, host, node.fullPath, node)}
        >
          {isExpandable ? (
            <FontAwesomeIcon icon={isExpanded ? faChevronDown : faChevronRight} className="text-[8px] w-3 flex-shrink-0" />
          ) : (
            <span className="w-3 flex-shrink-0" />
          )}
          <FontAwesomeIcon
            icon={hasChildren ? (isExpanded ? faFolderOpen : faFolder) : faFileCode}
            className={`flex-shrink-0 ${hasChildren ? 'text-yellow-500/70' : 'text-blue-400/70'}`}
            style={{ fontSize: '10px' }}
          />
          <span className="truncate flex-1" title={nodeFullUrl}>{name}</span>
          <ProfileDots entry={{ profiles: nodeProfileIds, anonymous: nodeAnon }} />
          {methods.length > 0 && (
            <div className="flex gap-0.5 flex-shrink-0">
              {methods.map(m => (
                <span key={m} className={`text-[8px] font-mono ${methodColors[m] || 'text-gray-400'}`}>{m}</span>
              ))}
            </div>
          )}
        </div>
        {isExpanded && (
          <>
            {Object.entries(node.children)
              .sort(([a], [b]) => a.localeCompare(b))
              .map(([childName, child]) => renderPathNode(child, childName, host, depth + 1))
            }
            {/* Parameter variant sub-items (Burp-style) */}
            {hasVariants && node.entries
              .sort((a, b) => (a.method || '').localeCompare(b.method || '') || (a.paramSig || '').localeCompare(b.paramSig || ''))
              .map(entry => renderVariantNode(entry, host, node.fullPath, depth + 1))
            }
          </>
        )}
      </div>
    );
  };

  const hostNames = Object.keys(tree).sort();

  return (
    <div className="h-full flex flex-col">
      <div className={`p-2 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
        <div className="relative">
          <FontAwesomeIcon icon={faSearch} className={`absolute left-2 top-1.5 text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`} />
          <input
            type="text" placeholder="Filter..." value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className={`w-full pl-7 pr-2 py-1 text-xs rounded border ${
              isDarkMode ? 'bg-gray-900 border-gray-600 text-gray-300 placeholder-gray-500' : 'bg-gray-50 border-gray-300 text-gray-700 placeholder-gray-400'
            }`}
          />
        </div>
        <div className="flex items-center justify-between mt-1">
          <span className={`text-[10px] ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
            {totalEndpoints} endpoints - {hostNames.length} hosts
          </span>
          <div className="flex items-center gap-1.5">
            {selectedFilter && (
              <button
                onClick={() => onSelect?.(null)}
                className={`flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded ${
                  isDarkMode ? 'bg-teal-900/30 text-teal-400 hover:bg-teal-900/50' : 'bg-teal-50 text-teal-600 hover:bg-teal-100'
                }`}
              >
                <FontAwesomeIcon icon={faEye} className="text-[8px]" /> Show All
              </button>
            )}
            {proxySessionId && outOfScopeHosts.length > 0 && (
              <button
                onClick={() => setShowOutOfScope(v => !v)}
                title="Hosts the proxy saw but blocked as out-of-scope. Toggle to show/hide; right-click one to add it to scope."
                className={`flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded ${
                  isDarkMode ? 'bg-amber-900/30 text-amber-400 hover:bg-amber-900/50' : 'bg-amber-50 text-amber-600 hover:bg-amber-100'
                }`}
              >
                <FontAwesomeIcon icon={showOutOfScope ? faEye : faEyeSlash} className="text-[8px]" />
                {outOfScopeHosts.length} out-of-scope
              </button>
            )}
            {proxyRunning && (
              <span className="flex items-center gap-1 text-[10px] text-green-400">
                <span className="w-1 h-1 rounded-full bg-green-400 animate-pulse" />
                Live
              </span>
            )}
          </div>
        </div>
        {multiSelected.size > 0 && (
          <div className={`flex items-center justify-between mt-1.5 px-2 py-1 rounded ${isDarkMode ? 'bg-emerald-900/30' : 'bg-emerald-50'}`}>
            <span className={`text-[10px] font-medium ${isDarkMode ? 'text-emerald-300' : 'text-emerald-700'}`}>
              {multiSelected.size} selected
            </span>
            <div className="flex items-center gap-1.5">
              <button
                onClick={handleMultiScan}
                className="flex items-center gap-1 text-[10px] px-2 py-0.5 rounded bg-emerald-600 text-white hover:bg-emerald-700"
              >
                <FontAwesomeIcon icon={faBolt} className="text-[8px]" /> Active Scan
              </button>
              <button
                onClick={clearMultiSelect}
                className={`text-[10px] px-1.5 py-0.5 rounded ${isDarkMode ? 'text-gray-400 hover:bg-gray-700' : 'text-gray-500 hover:bg-gray-100'}`}
              >
                Clear
              </button>
            </div>
          </div>
        )}
        {multiSelected.size === 0 && (
          <div className={`text-[9px] mt-1 ${isDarkMode ? 'text-gray-600' : 'text-gray-400'}`}>
            Tip: Ctrl/Cmd-click nodes to multi-select, then Active Scan.
          </div>
        )}
      </div>
      <div className="flex-1 overflow-y-auto py-1">
        {hostNames.length === 0 && (
          <div className={`text-center py-6 text-xs ${isDarkMode ? 'text-gray-600' : 'text-gray-400'}`}>
            No traffic captured yet
          </div>
        )}
        {hostNames.map(host => {
          const hostNode = tree[host];
          const hostKey = `host|${host}`;
          const isExpanded = expandedPaths.has(hostKey);
          const selected = isSelected(host, null);
          const entryCount = entries.filter(e => e.host === host).length;
          const hostColor = resolveColor(host, null);

          return (
            <div key={host}>
              <div
                className={`flex items-center gap-1 px-2 py-0.5 cursor-pointer text-xs font-medium ${
                  selected
                    ? (isDarkMode ? 'bg-teal-900/30 text-teal-300' : 'bg-teal-50 text-teal-700')
                    : (isDarkMode ? 'hover:bg-gray-700 text-gray-300' : 'hover:bg-gray-100 text-gray-700')
                }`}
                style={{
                  ...(hostColor && !selected ? { borderLeft: `3px solid ${hostColor}`, backgroundColor: `${hostColor}15` } : {})
                }}
                onClick={() => { onSelect?.({ host, path: null }); toggleExpand(hostKey); }}
                onContextMenu={(e) => handleTreeContextMenu(e, host, null, hostNode)}
              >
                <FontAwesomeIcon icon={isExpanded ? faChevronDown : faChevronRight} className="text-[8px] w-3 flex-shrink-0" />
                <FontAwesomeIcon icon={faGlobe} className="text-teal-500/70 flex-shrink-0" style={{ fontSize: '10px' }} />
                <span className="truncate flex-1" title={host}>{host}</span>
                {activeScanHosts?.[host] && (
                  <span className="flex items-center gap-0.5 text-[9px] text-orange-400 flex-shrink-0" title={`ExposIQ scan: ${activeScanHosts[host].progress || 0}%`}>
                    <FontAwesomeIcon icon={faSpinner} spin className="text-[8px]" />
                    {activeScanHosts[host].progress || 0}%
                  </span>
                )}
                <span className={`text-[9px] ${isDarkMode ? 'text-gray-600' : 'text-gray-400'}`}>{entryCount}</span>
              </div>
              {isExpanded && (
                <>
                  {hostNode.entries.length > 0 && hostNode.entries.map((entry, i) => {
                    const rootColor = resolveColor(host, '/');
                    return (
                      <div
                        key={`${host}|/|${i}`}
                        className={`flex items-center gap-1 pr-2 py-0.5 cursor-pointer text-xs ${
                          isSelected(host, '/')
                            ? (isDarkMode ? 'bg-teal-900/30 text-teal-300' : 'bg-teal-50 text-teal-700')
                            : (isDarkMode ? 'hover:bg-gray-700 text-gray-300' : 'hover:bg-gray-100 text-gray-70')
                        }`}
                        style={{
                          paddingLeft: 22,
                          ...(rootColor && !isSelected(host, '/') ? { borderLeft: `3px solid ${rootColor}`, backgroundColor: `${rootColor}15` } : {})
                        }}
                        onClick={() => onSelect?.({ host, path: '/' })}
                        onContextMenu={(e) => handleTreeContextMenu(e, host, '/', { children: {}, entries: [entry], fullPath: '/' })}
                      >
                        <span className="w-3 flex-shrink-0" />
                        <FontAwesomeIcon icon={faFileCode} className="text-blue-400/70 flex-shrink-0" style={{ fontSize: '10px' }} />
                        <span className="truncate flex-1" title={`https://${host}${entry.path || '/'}`}>/</span>
                        <ProfileDots entry={entry} />
                        {entry.methods?.map(m => (
                          <span key={m} className={`text-[8px] font-mono ${methodColors[m] || 'text-gray-400'}`}>{m}</span>
                        ))}
                      </div>
                    );
                  })}
                  {Object.entries(hostNode.children)
                    .sort(([a], [b]) => a.localeCompare(b))
                    .map(([name, child]) => renderPathNode(child, name, host, 1))
                  }
                </>
              )}
            </div>
          );
        })}

        {/* Out-of-scope hosts the proxy saw but blocked — faded, add to scope on demand */}
        {proxySessionId && showOutOfScope && outOfScopeHosts.length > 0 && (
          <div className={`mt-2 pt-2 border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
            <div className={`px-2 text-[9px] uppercase tracking-wide mb-1 ${isDarkMode ? 'text-amber-500/70' : 'text-amber-600/70'}`}>
              Out of scope ({outOfScopeHosts.length}) — blocked, not recorded
            </div>
            {outOfScopeHosts.map(h => (
              <div
                key={h.host}
                onContextMenu={(e) => {
                  e.preventDefault(); e.stopPropagation();
                  setContextMenu({ position: { x: e.clientX, y: e.clientY }, items: [
                    { label: 'Add host to proxy scope', icon: faPlus, onClick: () => handleAddScope(h.host) },
                    { label: 'Copy host', icon: faCopy, onClick: () => navigator.clipboard.writeText(h.host) }
                  ] });
                }}
                title={`Seen ${h.count}x — right-click or double-click to add to scope`}
                onDoubleClick={() => handleAddScope(h.host)}
                className={`group flex items-center gap-1.5 px-2 py-0.5 text-xs cursor-pointer ${isDarkMode ? 'text-gray-500 hover:bg-gray-800/60' : 'text-gray-400 hover:bg-gray-100'}`}
              >
                <FontAwesomeIcon icon={faGlobe} className="text-[9px] opacity-60" />
                <span className="truncate italic flex-1" title={h.host}>{h.host}</span>
                <span className="text-[9px] opacity-60">{h.count}×</span>
                <button
                  onClick={(e) => { e.stopPropagation(); handleAddScope(h.host); }}
                  className={`opacity-0 group-hover:opacity-100 text-[9px] px-1 rounded ${isDarkMode ? 'bg-teal-800/60 text-teal-300 hover:bg-teal-700' : 'bg-teal-100 text-teal-700 hover:bg-teal-200'}`}
                  title="Add to proxy scope"
                >+ scope</button>
              </div>
            ))}
          </div>
        )}
      </div>

      {contextMenu && (
        <ContextMenu
          items={contextMenu.items}
          position={contextMenu.position}
          onClose={() => setContextMenu(null)}
          isDarkMode={isDarkMode}
        />
      )}

      {scanConfigDialog && (
        <ScanConfigDialog
          initialConfig={scanConfigDialog}
          projectId={projectId}
          onStart={handleStartScan}
          onCancel={() => setScanConfigDialog(null)}
        />
      )}
    </div>
  );
};

export default SiteMapTree;
